
import { defineComponent, reactive, toRefs, onMounted, onActivated } from "vue";
import { useI18n } from "vue-i18n";
import { ElMessage, ElNotification } from "element-plus";
import _ from "lodash";
import API from "@/apis/TPSAPI";
import { APIResultCode } from "@/models/enums/APIResultCode";
import { usePaginatorSetup } from "@/stores/PaginatorStore";
import { DataTableType } from "@/models/enums/DataTableType";
import IResTPSPerformanceDataViewDTO from "@/models/dto/IResTPSPerformanceDataViewDTO";
import IResSiteInfoDTO from "@/models/dto/IResSiteInfoDTO";
import TPSPerformanceSearchCriteria from "@/models/TPSPerformanceSearchCriteria";
import { useUserInfoStore } from "@/stores/UserInfoStore";

export default defineComponent({
  setup() {
    const { t } = useI18n();
    const paginatorSetup = usePaginatorSetup();
    const userInfoStore = useUserInfoStore();
    const tableType = DataTableType.tpsPerformance;
    const PageSizeList = [20, 50, 100];

    const state = reactive({
      isPageLoading: false,
      isSiteLoading: false,

      // pagination
      filteredTotal: 0,
      pageSize: PageSizeList,

      /* Component Data */
      displayList: [] as IResTPSPerformanceDataViewDTO[],
      filterdListLength: 0,

      /* 搜尋條件 Search Condition */
      searchCriteria: new TPSPerformanceSearchCriteria(1, PageSizeList[0]),

      // 匯出用的搜尋條件
      exportCriteria: new TPSPerformanceSearchCriteria(1, PageSizeList[0]),

      //廠區清單
      sites: [] as IResSiteInfoDTO[],

      colDisplayCtrl: new Array(50).fill(true) as boolean[],
    });

    //#region Hook functions
    onMounted(async () => {
      PageInit();
    });

    onActivated(async () => {
      PageInit();
    });

    const PageInit = _.throttle(async function () {
      try {
        state.isPageLoading = true;
        await updateUserPreference();
        state.searchCriteria.lastPeriodCnt = 1;
        await updateSearchResultListAsync();
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isPageLoading = false;
      }
    }, 100);
    //#endregion

    //#region UI Events

    /** 顯示廠區下拉選單時 */
    const onSiteListShown = async () => {
      try {
        state.isSiteLoading = true;
        await updateSiteList();
      } catch (error) {
        console.log(error);
      } finally {
        state.isSiteLoading = false;
      }
    };

    /** 按下並放開Enter鍵 */
    const onInputFieldEnterKeyUp = async () => {
      await updateSearchResultListAsync();
    };

    /** 按下 清理 搜尋條件 按鈕 */
    const onBtnClearSearchClicked = () => {
      state.searchCriteria.clear();
    };

    /** 按下 搜尋 按鈕 */
    const onBtnSearchTableListClicked = async () => {
      try {
        /* 刷新資料 */
        await updateSearchResultListAsync();
      } catch (error) {
        console.error(error);
      }
    };

    /** 按下匯出按鈕 */
    const onExportReportClicked = async () => {
      try {
        state.isPageLoading = true;
        console.log(state.exportCriteria);
        const exportResponse = await API.exportTPSPerformanceReport(state.exportCriteria.ToReqDTO());
        if (exportResponse.data.result !== 1) {
          ElNotification({
            title: "錯誤",
            dangerouslyUseHTMLString: true,
            message: t("export_failed") + `((:<br>${exportResponse.data.msg}`,
            type: "error",
          });
          return;
        }

        const FileName = exportResponse.data.content;
        const DownloadReportResponse = await API.downloadReportFile(FileName);

        var blob = new Blob([DownloadReportResponse.data], { type: "application/" + DownloadReportResponse.headers["content-type"] });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        document.body.appendChild(a);
        a.setAttribute("style", "display: none");
        a.href = url;
        a.download = FileName;
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove(); // remove the element
      } catch (error) {
        console.log(error);
      } finally {
        state.isPageLoading = false;
      }
    };

    /** 改變排序 */
    const onSortChanged = async (sort: any) => {
      //console.log(`onSortChanged: ${sort.column}, ${sort.prop}, ${sort.order}`);
      try {
        state.searchCriteria.sortColName = sort.prop;
        state.searchCriteria.sortOrder = sort.order;
        await updateSearchResultListAsync();
        window.scrollTo(0, 0);
      } catch (error) {
        console.log(error);
      } finally {
        state.isPageLoading = false;
      }
    };

    /** 分頁輸入 */
    const onPagesizeChanged = () => {
      updateSearchResultListAsync();
    };

    const onCurrentPageChanged = () => {
      updateSearchResultListAsync();
    };

    /** 關閉欄位顯示設定Panel時 */
    const onColDisplayCtrlPanelHidded = async () => {
      await API.patchTableDisplayPreference(tableType, { displayMap: state.colDisplayCtrl });
    };
    //#endregion

    //#region Private Functions
    /** 更新使用者設置偏好 */
    const updateUserPreference = async () => {
      const getPrefResponse = await API.getTableDisplayPreference(tableType);
      if (getPrefResponse.data.result !== 1) {
        console.log(`updateUserPreference : ${getPrefResponse.data.msg}`);
      }
      state.colDisplayCtrl = getPrefResponse.data.content;
    };

    /** 呼叫API, 更新廠區清單 */
    const updateSiteList = async () => {
      try {
        const getSiteResult = await API.getAllSites();

        if (getSiteResult.data.result !== APIResultCode.success) {
          throw new Error(getSiteResult.data.msg);
        }

        state.sites = getSiteResult.data.content;
      } catch (error) {
        console.log(error);
      }
    };

    /** 更新顯示清單 */
    const updateSearchResultListAsync = async () => {
      try {
        /* Table Loading Flag 開啟 */
        state.isPageLoading = true;

        copySearchCriteria();

        const searchResult = await API.searchTPSPerformanceReport(state.searchCriteria.ToReqDTO());
        if (searchResult.data.result !== APIResultCode.success) {
          throw new Error(searchResult.data.msg);
        }

        //取得改善事項清單
        state.displayList = searchResult.data.content.pageItems;

        //更新分頁資料總數
        state.filteredTotal = searchResult.data.content.totalItems;
      } catch (error: any) {
        console.error(error);
        ElMessage({
          type: "error",
          message: t("errorMessage"),
        });
      } finally {
        state.isPageLoading = false;
      }
    };

    /** 備份搜尋條件(供匯出使用) */
    function copySearchCriteria() {
      state.exportCriteria = new TPSPerformanceSearchCriteria(state.searchCriteria.page, state.searchCriteria.pageSize);
      state.exportCriteria.lastPeriodCnt = state.searchCriteria.lastPeriodCnt;
      state.exportCriteria.site = state.searchCriteria.site;
      state.exportCriteria.factory = state.searchCriteria.factory;
      state.exportCriteria.projectCodeKeyword = state.searchCriteria.projectCodeKeyword;
      state.exportCriteria.pnKeyword = state.searchCriteria.pnKeyword;

    }

    //#endregion
    return {
      ...toRefs(state),
      paginatorSetup,
      userInfoStore,
      t,

      onInputFieldEnterKeyUp,
      onExportReportClicked,
      onBtnClearSearchClicked,
      onBtnSearchTableListClicked,
      onSiteListShown,
      onColDisplayCtrlPanelHidded,
      onSortChanged,
      onPagesizeChanged,
      onCurrentPageChanged,
    };
  },
});
